import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Save, Flag, Gauge, Zap } from "lucide-react";
import { api } from "@/lib/api";

// iter86.32 — RapidAPI monthly quota banner (headers captured on every upstream call).
function QuotaBanner({ quota }) {
    if (!quota || quota.limit == null) return (
        <p className="text-[11px] text-zinc-600 mb-3" data-testid="golf-quota-empty">
            RapidAPI quota: no reading yet — appears after the first upstream call.
        </p>
    );
    const pct = quota.limit ? quota.remaining / quota.limit : 1;
    const tone = pct < 0.1 ? "text-red-400 border-red-500/40 bg-red-500/10"
        : pct < 0.25 ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
        : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
    let resetStr = "";
    try {
        const resetAt = new Date(new Date(quota.captured_at).getTime() + quota.reset_seconds * 1000);
        resetStr = resetAt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { /* best-effort */ }
    const asOf = quota.captured_at
        ? new Date(quota.captured_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : null;
    return (
        <div className={`border rounded px-3 py-2 mb-3 ${tone}`} data-testid="golf-quota-banner">
            <div className="flex items-center gap-2 text-xs font-bold">
                <Gauge className="w-3.5 h-3.5" />
                {quota.remaining.toLocaleString()} of {quota.limit.toLocaleString()} RapidAPI calls left this cycle
            </div>
            <div className="text-[10px] opacity-80 mt-0.5">
                {resetStr && <>Quota resets ~{resetStr}</>}{asOf && <> · reading as of {asOf}</>}
            </div>
        </div>
    );
}

// iter86.31.7 — Slash Golf poller cadence (mirrors the API-Football knob).
export default function SlashGolfPollerCadenceCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({ live_sec: "", field_sec: "" });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.siteGetGolfPollerCadence();
            setData(d);
            setDraft({
                live_sec: d.overrides.includes("live_sec") ? String(d.live_sec) : "",
                field_sec: d.overrides.includes("field_sec") ? String(d.field_sec) : "",
            });
        } catch {
            toast.error("Failed to load golf poller cadence");
        }
        setLoading(false);
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const onSave = async () => {
        setSaving(true);
        try {
            const body = {
                live_sec: draft.live_sec === "" ? null : Number(draft.live_sec),
                field_sec: draft.field_sec === "" ? null : Number(draft.field_sec),
            };
            await api.siteUpdateGolfPollerCadence(body);
            toast.success("Golf cadence saved — applies on the next poller wake");
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="brutal-card p-4" data-testid="golf-poller-cadence-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    <Flag className="w-4 h-4 text-volt" /> Slash Golf Poller Cadence
                </h3>
                <button onClick={reload} disabled={loading}
                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                        data-testid="golf-poller-cadence-refresh">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
                </button>
            </div>
            {!data ? (
                <p className="text-xs text-zinc-500">Loading…</p>
            ) : (
                <>
                    <QuotaBanner quota={data.quota} />
                    <p className="text-xs text-zinc-400 mb-2">
                        RapidAPI leaderboard/field cache TTLs. Blank = env default. Applies on the
                        next poller wake (within <span className="font-mono">{data.wake_interval_sec}s</span>).
                    </p>
                    <p className="text-[11px] mb-4 flex items-start gap-1.5" data-testid="golf-dynamic-cadence-note">
                        <Zap className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${data.dynamic_live ? "text-volt" : "text-zinc-600"}`} />
                        {data.dynamic_live ? (
                            <span className="text-zinc-400">
                                <span className="text-volt font-bold">Final-round boost active:</span>{" "}
                                early rounds poll every {Math.round(data.defaults.live_sec / 60)}m; on the
                                event's final day (Sunday) the poller automatically tightens to{" "}
                                {Math.round(data.final_round_sec / 60)}m. Setting a Live TTL override disables the boost.
                            </span>
                        ) : (
                            <span className="text-zinc-500">
                                Final-round boost <span className="font-bold">off</span> — your Live TTL override
                                applies to every round. Clear it to restore the automatic Sunday{" "}
                                {Math.round(data.final_round_sec / 60)}m cadence.
                            </span>
                        )}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        <label className="text-xs text-zinc-400 space-y-1">
                            <span className="font-bold uppercase text-zinc-300">Live TTL (sec)</span>
                            <input data-testid="golf-cadence-live-sec" type="number" value={draft.live_sec}
                                placeholder={String(data.defaults.live_sec)}
                                onChange={(e) => setDraft(p => ({ ...p, live_sec: e.target.value }))}
                                className="brutal-input w-full font-mono text-sm" />
                            <span className="block text-[10px] text-zinc-600">
                                Leaderboard refresh during tournament days. Floor 60s. Current: {data.live_sec}s
                                {data.overrides.includes("live_sec") ? " (override)" : " (default)"}
                            </span>
                        </label>
                        <label className="text-xs text-zinc-400 space-y-1">
                            <span className="font-bold uppercase text-zinc-300">Field TTL (sec)</span>
                            <input data-testid="golf-cadence-field-sec" type="number" value={draft.field_sec}
                                placeholder={String(data.defaults.field_sec)}
                                onChange={(e) => setDraft(p => ({ ...p, field_sec: e.target.value }))}
                                className="brutal-input w-full font-mono text-sm" />
                            <span className="block text-[10px] text-zinc-600">
                                Pre-event entry-list refresh. Floor 1h. Current: {data.field_sec}s
                                {data.overrides.includes("field_sec") ? " (override)" : " (default)"}
                            </span>
                        </label>
                    </div>
                    <button onClick={onSave} disabled={saving}
                            className="px-3 py-1.5 bg-volt/20 border border-volt text-volt rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-volt/30 disabled:opacity-50"
                            data-testid="golf-poller-cadence-save">
                        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Cadence"}
                    </button>
                </>
            )}
        </div>
    );
}
